class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.countdownInterval = null;
    this.announcementElement = null;
    this.lastAnnouncement = '';
  }

  connectedCallback() {
    this.render();
    this.initializeCountdown();
    this.setupAccessibility();
  }

  disconnectedCallback() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  render() {
    const countdownDate = this.getAttribute('data-countdown');
    // Render method can be used for initial setup if needed
  }

  setupAccessibility() {
    // Add keyboard support for better navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.announceCurrentTime();
      }

      // Add arrow key support for navigation
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        this.announceCurrentTime();
      }
    });

    // Find the announcement element
    this.announcementElement = this.querySelector('[data-countdown-announcement]');

    // Set initial ARIA attributes
    this.setAttribute('role', 'timer');
  }

  announceCurrentTime() {
    if (this.announcementElement) {
      const days = this.querySelector('[data-days]')?.textContent || '00';
      const hours = this.querySelector('[data-hours]')?.textContent || '00';
      const minutes = this.querySelector('[data-minutes]')?.textContent || '00';
      const seconds = this.querySelector('[data-seconds]')?.textContent || '00';
      const flashTimerText = this.querySelector('.flash-timer-text')?.textContent || '00:00:00';
      const replaceDaysWithHours = this.getAttribute('data-replace-days-with-hours') === "true";

      // Different announcements based on location
      const location = this.getAttribute('data-location');
      let announcement = '';

      if (location === 'product-grid') {
        // For flash sale badges, use a more concise announcement
        const [hoursStr, minutesStr, secondsStr] = flashTimerText.split(':');
        announcement = `Flash sale ends in ${hoursStr} hours, ${minutesStr} minutes, ${secondsStr} seconds`;
      } else {
        // For other locations, use the full announcement
        if (replaceDaysWithHours) {
          announcement = `Countdown: ${hours} hours, ${minutes} minutes, ${seconds} seconds remaining`;
        } else {
          announcement = `Countdown: ${days} days, ${hours} hours, ${minutes} minutes, ${seconds} seconds remaining`;
        }
      }

      this.announcementElement.textContent = announcement;

      // Update the main aria-label for better screen reader context
      this.setAttribute('aria-label', announcement);
    }
  }

  showTimerEnded() {
    // Show "Ended" message for collection timers
    const timerWrapper = this.querySelector('.flash-sale-timer')
      ? this.querySelector('.flash-sale-timer')
      : this;
    const timerEndText = this.getAttribute('data-timer-end-text');
    if (timerWrapper) {
      // Hide the countdown timer elements first
      const countdownElements = timerWrapper.querySelectorAll(
        '.time-text-wrapper, .hours-text-wrapper, .minutes-text-wrapper, .seconds-text-wrapper'
      );
    }

    // Update accessibility
    this.setAttribute('aria-label', 'Countdown timer - Sale has ended');

    // Show flash sale email collectors when timer ends
    if (window.FlashSaleEmailCollector) {
      setTimeout(() => {
        window.FlashSaleEmailCollector.showAllCollectors();
      }, 1000);
    }
  }

  initializeCountdown() {
    // Get separate date and time fields
    const startDate = this.getAttribute('data-start-date'); // e.g., "July 2, 2025"
    const startTime = this.getAttribute('data-start-time'); // e.g., "12:00 AM"
    const endDate = this.getAttribute('data-end-date'); // e.g., "July 8, 2025"
    const endTime = this.getAttribute('data-end-time'); // e.g., "1:17 PM"
    const location = this.getAttribute('data-location'); // e.g., "product", "collection", "product-grid"
    const timerStyle = this.getAttribute('data-timer-style');
    const hideAfterText = this.getAttribute('data-timer-hide-aftertext') === "true";
    const replaceDaysWithHours = this.getAttribute('data-replace-days-with-hours') === "true";

    // Combine date and time for Date parsing
    const startDateTimeString = startDate && startTime ? `${startDate} ${startTime}` : null;
    const endDateTimeString = endDate && endTime ? `${endDate} ${endTime}` : null;

    const startDateTime = startDateTimeString ? _theme.Helpers.parseESTDate(startDate, startTime).getTime() : 0;
    const countDownDate = endDateTimeString ? _theme.Helpers.parseESTDate(endDate, endTime).getTime() : 0;

    const now = _theme.Helpers.getCurrentESTTime();

    const daysElement = this.querySelector('[data-days]');
    const hoursElement = this.querySelector('[data-hours]');
    const minutesElement = this.querySelector('[data-minutes]');
    const secondsElement = this.querySelector('[data-seconds]');
    const flashTimerText = this.querySelector('.flash-timer-text');

    if (isNaN(countDownDate)) {
      this.innerHTML = '';
      this.setAttribute('aria-label', 'Countdown timer - Invalid date');
      return;
    }

    // Check if we're before the start date
    if (startDate && now < startDateTime) {
      this.style.display = 'none';
      this.setAttribute('aria-label', 'Countdown timer - Not started yet');
      // Find and remove the parent element with data-remove-element attribute
      const parentElement = this.closest('[data-remove-element]');
      if (parentElement) {
        parentElement.remove();
      }
      return;
    }

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const updateInterval = prefersReducedMotion ? 5000 : 1000; // Update less frequently if reduced motion

    const updateCountdown = () => {
      const currentTime = _theme.Helpers.getCurrentESTTime();

      // Check if we're before the start date
      if (startDate && currentTime < startDateTime) {
        this.style.display = 'none';
        // Find and remove the parent element with data-remove-element attribute
        const parentElement = this.closest('[data-remove-element]');
        if (parentElement) {
          parentElement.remove();
        }
        this.setAttribute('aria-label', 'Countdown timer - Not started yet');
        return;
      }

      // Show the countdown if we're after the start date
      if (startDate && currentTime >= startDateTime) {
        this.style.display = 'flex';
      }

      const distance = countDownDate - currentTime;

      if (distance < 0) {

        // Stop the interval
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }

        // Check if we already reloaded once
        const buffer = 60000; // 1 minute
        const endTime = countDownDate; // countDownDate is already a timestamp
        const storageKey = `isCountdownTimerEnd_${countDownDate}`;

        if (currentTime >= endTime && currentTime <= endTime + buffer && !sessionStorage.getItem(storageKey)) {
          sessionStorage.setItem(storageKey, 'true');
          window.location.reload(); // reload page once
          return; // stop further execution
        }

        const timerIsEndText = this.getAttribute('data-show-end-text');

        if (timerIsEndText === 'true' || timerIsEndText === true) {
          this.showTimerEnded();
          // Find and remove the parent element with data-remove-element attribute
          const parentElement = this.closest('[data-remove-element]');
          console.log('Removing parent element:', parentElement);
          if (parentElement) {
            parentElement.remove();
          }
        } else {
          // For non-collection timers, hide everything like before
          this.setAttribute('aria-label', 'Countdown timer - Time expired');
          // Find and remove the parent element with data-remove-element attribute
          const parentElement = this.closest('[data-remove-element]');
          if (parentElement) {
            parentElement.remove();
          }
        }

        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
        return;
      }

      let days = Math.floor(distance / (1000 * 60 * 60 * 24));
      let hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      // Convert days to hours if replace days with hours is enabled
      if (replaceDaysWithHours) {
        hours = hours + (days * 24);
        days = 0; // Hide days when replacing with hours
      }

      // Handle product-grid location (flash sale badge format)
      if (location === 'product-grid' && flashTimerText) {
        const daysText = days < 10 ? '0' + days : days.toString();
        const hoursText = hours < 10 ? '0' + hours : hours.toString();
        const minutesText = minutes < 10 ? '0' + minutes : minutes.toString();
        const secondsText = seconds < 10 ? '0' + seconds : seconds.toString();

        if(hideAfterText || hideAfterText === "true"){
          if(timerStyle === 'second'){
            if (replaceDaysWithHours) {
              flashTimerText.textContent = `${hoursText} : ${minutesText} : ${secondsText}`;
            } else {
              flashTimerText.textContent = `${daysText}:${hoursText}:${minutesText}:${secondsText}`;
            }
          }else{
            if (replaceDaysWithHours) {
              flashTimerText.textContent = `${hoursText} : ${minutesText} : ${secondsText}`;
            } else {
              flashTimerText.textContent = `${daysText} : ${hoursText} : ${minutesText}`;
            }
          }
          
        }else{
          if(timerStyle === 'second'){
            if (replaceDaysWithHours) {
              flashTimerText.textContent = `${hoursText}h : ${minutesText}m : ${secondsText}s`;
            } else {
              flashTimerText.textContent = `${daysText}d : ${hoursText}h : ${minutesText}m : ${secondsText}s`;
            }
          }else{
            if (replaceDaysWithHours) {
              flashTimerText.textContent = `${hoursText}h : ${minutesText}m : ${secondsText}s`;
            } else {
              flashTimerText.textContent = `${daysText}d : ${hoursText}h : ${minutesText}m`;
            }
          }
        }
        
        this.setAttribute(
          'aria-label',
          `Flash sale countdown: ${replaceDaysWithHours ? hoursText : daysText + ' ' + hoursText} hours, ${minutesText} minutes, ${secondsText} seconds remaining`
        );
        return;
      }

      // Update visual elements for other locations
      if (daysElement) {
        if (replaceDaysWithHours) {
          // Hide days element when replacing days with hours
          daysElement.style.display = 'none';
          const daysLabel = daysElement.parentElement.querySelector('.label');
          if (daysLabel) {
            daysLabel.style.display = 'none';
          }
          // Hide the days separator as well
          const daysSeparator = this.querySelector('.days-separator');
          if (daysSeparator) {
            daysSeparator.style.display = 'none';
          }
          // Hide the days separator as well
          const daysWrapper = daysElement.closest('.time-text-wrapper');
          if (daysWrapper) {
            daysWrapper.style.display = 'none';
          }
        } else {
          // Show days element normally
          daysElement.style.display = 'flex';
          const daysLabel = daysElement.parentElement.querySelector('.label');
          if (daysLabel) {
            daysLabel.style.display = 'block';
          }
          // Show the days separator
          const daysSeparator = this.querySelector('.days-separator');
          if (daysSeparator) {
            daysSeparator.style.display = 'inline';
          }
          const daysText = days < 10 ? '0' + days : days.toString();
          if(hideAfterText || hideAfterText === "true"){
            daysElement.textContent = daysText;
          }else{
            daysElement.textContent = daysText+'d';
          }
        }
      }

      if (hoursElement) {
        const hoursText = hours < 10 ? '0' + hours : hours.toString();
        if(hideAfterText || hideAfterText === "true"){
          hoursElement.textContent = hoursText;
        }else{
          hoursElement.textContent = hoursText+'h';
        }
      }

      if (minutesElement) {
        const minutesText = minutes < 10 ? '0' + minutes : minutes.toString();
        if(hideAfterText || hideAfterText === "true"){
          minutesElement.textContent = minutesText;
        }else{
          minutesElement.textContent = minutesText+'m';
        }
      }

      if (secondsElement) {
        const secondsText = seconds < 10 ? '0' + seconds : seconds.toString();
        if(hideAfterText || hideAfterText === "true"){
          secondsElement.textContent = secondsText;
        }else{
          secondsElement.textContent = secondsText+'s';
        }
      }

      // Update announcement for screen readers (only when significant changes occur)
      const currentAnnouncement = replaceDaysWithHours 
        ? `${hours} hours, ${minutes} minutes, ${seconds} seconds`
        : `${days} days, ${hours} hours, ${minutes} minutes, ${seconds} seconds`;

      // Announce every minute for reduced motion, or every 10 seconds for normal
      const shouldAnnounce = prefersReducedMotion
        ? minutes !== this.lastAnnouncementMinutes
        : seconds % 10 === 0 && currentAnnouncement !== this.lastAnnouncement;

      if (shouldAnnounce) {
        this.announceCurrentTime();
        this.lastAnnouncement = currentAnnouncement;
        this.lastAnnouncementMinutes = minutes;
      }

      // Update main aria-label
      this.setAttribute('aria-label', `Countdown timer: ${currentAnnouncement} remaining`);
    };

    updateCountdown();
    this.countdownInterval = setInterval(updateCountdown, updateInterval);
  }


}

customElements.define('countdown-timer', CountdownTimer);